class SearchForm extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input[type="search"]');
    this.resetButton = this.querySelector('button[type="reset"]');

    if (this.input) {
      this.input.form.addEventListener('reset', this.onFormReset.bind(this));
      this.input.addEventListener(
        'input',
        debounce((event) => {
          this.onChange(event);
        }, 300).bind(this)
      );
    }
  }

  toggleResetButton() {
    const resetIsHidden = this.resetButton.classList.contains('hidden');
    if (this.input.value.length > 0 && resetIsHidden) {
      this.resetButton.classList.remove('hidden');
    } else if (this.input.value.length === 0 && !resetIsHidden) {
      this.resetButton.classList.add('hidden');
    }
  }

  onChange() {
    this.toggleResetButton();
  }

  shouldResetForm() {
    return !document.querySelector('[aria-selected="true"] a');
  }

  onFormReset(event) {
    // Prevent default so the form reset doesn't set the value gotten from the url on page load
    event.preventDefault();
    // Don't reset if the user has selected an element on the predictive search dropdown
    if (this.shouldResetForm()) {
      this.input.value = '';
      this.input.focus();
      this.toggleResetButton();
    }
  }
}

customElements.define('search-form', SearchForm);

const clotheSearchPhrase = (term) => term.trim().toLowerCase().replace(/\s+/g, ' ').replace(/^(?:show me|find me|looking for|i am looking for|i'm looking for|shop for|shop)(?: the| a| some)? /, '');

const clotheCategorySearch = {
  categories: [
  {
    "key": "sweaters",
    "pattern": "sweaters?|pullovers?",
    "query": "(title:sweater OR title:pullover) AND -title:card"
  },
  {
    "key": "cardigans",
    "pattern": "cardigans?",
    "query": "(title:cardigan) AND -title:card"
  },
  {
    "key": "jeans",
    "pattern": "jeans?",
    "query": "(title:jeans OR title:jean) AND -title:card -title:shorts -title:jacket -title:vest -title:skirt -title:dress"
  },
  {
    "key": "pants",
    "pattern": "pants?|trousers?",
    "query": "(title:pants OR title:trouser) AND -title:card"
  },
  {
    "key": "shorts",
    "pattern": "shorts?",
    "query": "((title:shorts AND -title:\"short sleeve\") OR (title:shorts AND title:set)) AND -title:card"
  },
  {
    "key": "skirts",
    "pattern": "skirts?",
    "query": "(title:skirt) AND -title:card"
  },
  {
    "key": "jumpsuits",
    "pattern": "jumpsuits?",
    "query": "(title:jumpsuit) AND -title:card"
  },
  {
    "key": "dresses",
    "pattern": "dress(?:es)?",
    "query": "(title:dress) AND -title:card"
  },
  {
    "key": "boots",
    "pattern": "boots?|booties",
    "query": "(title:boot OR title:bootie) AND -title:card -title:holder -title:ornament -title:keychain -title:necklace -title:candle -title:matches -title:earring -title:sticker -title:pillow -title:mug -title:tumbler"
  },
  {
    "key": "sneakers",
    "pattern": "sneakers?|trainers?",
    "query": "(title:sneaker) AND -title:card -title:holder -title:ornament"
  },
  {
    "key": "sandals",
    "pattern": "sandals?",
    "query": "(title:sandal) AND -title:card -title:holder -title:ornament"
  },
  {
    "key": "heels",
    "pattern": "heels?",
    "query": "(title:heel) AND -title:card -title:holder -title:ornament"
  },
  {
    "key": "flats",
    "pattern": "flats?|ballet flats?",
    "query": "(title:flat) AND -title:card -title:holder -title:ornament"
  },
  {
    "key": "earrings",
    "pattern": "earrings?|studs?",
    "query": "(title:earring OR title:stud OR title:hoop) AND -title:card -title:necklace -title:tag -title:jeans -title:shorts -title:top -title:dress -title:skirt -title:belt"
  },
  {
    "key": "necklaces",
    "pattern": "necklaces?",
    "query": "(title:necklace) AND -title:card"
  },
  {
    "key": "bracelets",
    "pattern": "bracelets?",
    "query": "(title:bracelet OR title:bangle) AND -title:card"
  },
  {
    "key": "purses",
    "pattern": "purses?|handbags?",
    "query": "(title:purse OR title:handbag) AND -title:card -title:strap"
  },
  {
    "key": "totes",
    "pattern": "totes?|tote bags?",
    "query": "(title:tote) AND -title:card"
  },
  {
    "key": "clutches",
    "pattern": "clutch(?:es)?",
    "query": "(title:clutch) AND -title:card"
  },
  {
    "key": "candles",
    "pattern": "candles?",
    "query": "(title:candle) AND -title:card -title:holder -title:lighter"
  },
  {
    "key": "diffusers",
    "pattern": "diffusers?",
    "query": "(title:diffuser) AND -title:card"
  },
  {
    "key": "greeting_cards",
    "pattern": "greeting cards?|event cards?",
    "query": "(title:card) AND -title:gift -title:playing -title:case -title:wallet"
  }
],
  intent(term) {
    const normalized = clotheSearchPhrase(term);
    const colors = '(black|blue|red|pink|green|white|ivory|cream|brown|olive|navy|purple|yellow|orange|gray|grey|beige|tan|burgundy|maroon|chocolate)';
    for (const category of this.categories) {
      const product = '(?:' + category.pattern + ')';
      if (new RegExp('^' + product + '$').test(normalized)) return { category, color: '' };
      const match = normalized.match(new RegExp('^' + colors + ' ' + product + '$')) || normalized.match(new RegExp('^' + product + ' ' + colors + '$'));
      if (match) return { category, color: match[1] === 'grey' ? 'gray' : match[1] };
    }
    return null;
  },
  isQuery(term) {
    return this.categories.some((category) => term === category.query || term.startsWith(category.query + ' AND '));
  },
  url(term, action) {
    const intent = this.intent(term);
    if (!intent) return null;
    const url = new URL(action, window.location.origin);
    url.search = '';
    const colorQuery = intent.color === 'gray' ? '(title:gray OR title:grey)' : 'title:' + intent.color;
    const blueJeans = intent.category.key === 'jeans' && intent.color === 'blue';
    url.searchParams.set('q', intent.category.query + (intent.color && !blueJeans ? ' AND ' + colorQuery : ''));
    if (blueJeans) url.searchParams.set('filter.v.t.shopify.color-pattern', 'gid://shopify/FilterSettingGroup/130810078');
    url.searchParams.set('type', 'product');
    url.searchParams.set('options[prefix]', 'none');
    return url;
  }
};

const clotheTeeSearch = {
  query: "title:tee OR title:tshirt OR title:\"t-shirt\"",
  qualifiers(term) {
    const normalized = clotheSearchPhrase(term);
    const product = '(?:tees?|t[ -]?shirts?)';
    const match = normalized.match(new RegExp('^(.*?)\\b' + product + '(?: (.*))?$'));
    if (!match) return null;
    const words = (match[1] + ' ' + (match[2] || '')).trim().split(/\s+/).filter(Boolean);
    const colors = /^(black|blue|red|pink|green|white|ivory|cream|brown|olive|navy|purple|yellow|orange)$/;
    if (words.some((word) => word !== 'graphic' && !colors.test(word))) return null;
    const selectedColors = words.filter((word) => colors.test(word));
    const graphics = words.filter((word) => word === 'graphic');
    if (selectedColors.length > 1 || graphics.length > 1) return null;
    return selectedColors.concat(graphics);
  },
  matches(term) {
    return this.qualifiers(term) !== null;
  },
  isQuery(term) {
    return term === this.query || term.startsWith('(' + this.query + ') AND ');
  },
  url(term, action) {
    const qualifiers = this.qualifiers(term);
    if (qualifiers === null) return null;
    const url = new URL(action, window.location.origin);
    url.search = '';
    const query = qualifiers.length ? '(' + this.query + ')' +
      qualifiers.map((word) => ' AND ' + (word === 'graphic' ? word : 'title:' + word)).join('') + ' -title:dress' : this.query;
    url.searchParams.set('q', query);
    url.searchParams.set('type', 'product');
    url.searchParams.set('options[prefix]', 'none');
    return url;
  }
};

const clotheSweatshirtSearch = {
  query: 'title:sweatshirt -title:card',
  qualifier(term) {
    const normalized = clotheSearchPhrase(term);
    const product = '(?:sweat[ -]?shirts?|sweatshrits?)';
    const color = '(black|blue|red|pink|green|white|ivory|cream|brown|olive|navy|purple|yellow|orange)';
    if (new RegExp('^' + product + '$').test(normalized)) return '';
    const match = normalized.match(new RegExp('^' + color + ' ' + product + '$')) ||
      normalized.match(new RegExp('^' + product + ' ' + color + '$'));
    return match ? match[1] : null;
  },
  matches(term) {
    return this.qualifier(term) !== null;
  },
  isQuery(term) {
    return term === this.query || term.startsWith('title:sweatshirt AND title:') && term.endsWith(' -title:card');
  },
  url(term, action) {
    const color = this.qualifier(term);
    if (color === null) return null;
    const url = new URL(action, window.location.origin);
    url.search = '';
    url.searchParams.set('q', color ? 'title:sweatshirt AND title:' + color + ' -title:card' : this.query);
    url.searchParams.set('type', 'product');
    url.searchParams.set('options[prefix]', 'none');
    return url;
  }
};

const clotheHoodieSearch = {
  query: 'title:hoodie -title:card',
  qualifier(term) {
    const normalized = clotheSearchPhrase(term);
    const product = '(?:hoodie|hoodies|hoody)';
    const color = '(black|blue|red|pink|green|white|ivory|cream|brown|olive|navy|purple|yellow|orange|grey|gray)';
    if (new RegExp('^' + product + '$').test(normalized)) return '';
    const match = normalized.match(new RegExp('^' + color + ' ' + product + '$')) ||
      normalized.match(new RegExp('^' + product + ' ' + color + '$'));
    return match ? match[1] : null;
  },
  matches(term) {
    return this.qualifier(term) !== null;
  },
  isQuery(term) {
    return term === this.query || term.startsWith('title:hoodie AND title:') && term.endsWith(' -title:card');
  },
  url(term, action) {
    const color = this.qualifier(term);
    if (color === null) return null;
    const url = new URL(action, window.location.origin);
    url.search = '';
    url.searchParams.set('q', color ? 'title:hoodie AND title:' + color + ' -title:card' : this.query);
    url.searchParams.set('type', 'product');
    url.searchParams.set('options[prefix]', 'none');
    return url;
  }
};

const clotheSetSearch = {
  query: 'matching set',
  matches(term) {
    return /^co[ -]?ords?(?: sets?)?$/.test(clotheSearchPhrase(term));
  },
  url(term, action) {
    if (!this.matches(term)) return null;
    const url = new URL(action, window.location.origin);
    url.search = '';
    url.searchParams.set('q', this.query);
    url.searchParams.set('options[prefix]', 'last');
    return url;
  }
};

const clotheProductSearch = {
  query: 'title:romper',
  qualifier(term) {
    const normalized = clotheSearchPhrase(term);
    const product = '(?:play[ -]?suits?|rompers?)';
    const color = '(black|blue|red|pink|green|white|ivory|cream|brown|olive|navy|purple|yellow|orange)';
    if (new RegExp('^' + product + '$').test(normalized)) return '';
    const match = normalized.match(new RegExp('^' + color + ' ' + product + '$')) ||
      normalized.match(new RegExp('^' + product + ' ' + color + '$'));
    return match ? match[1] : null;
  },
  matches(term) {
    return this.qualifier(term) !== null;
  },
  url(term, action) {
    const qualifier = this.qualifier(term);
    if (qualifier === null) return null;
    const url = new URL(action, window.location.origin);
    url.search = '';
    url.searchParams.set('q', this.query + (qualifier ? ' AND title:' + qualifier : ''));
    url.searchParams.set('type', 'product');
    url.searchParams.set('filter.v.availability', '1');
    return url;
  }
};

const clotheDressSearch = {
  occasions: {
  "casual": "gid://shopify/Metaobject/55879532766",
  "everyday": "gid://shopify/Metaobject/55880220894",
  "formal": "gid://shopify/Metaobject/55881171166",
  "holiday": "gid://shopify/Metaobject/138160505054",
  "party": "gid://shopify/Metaobject/57503351006",
  "vacation": "gid://shopify/FilterSettingGroup/130154718"
},
  url(term, action) {
    const normalized = term.trim().toLowerCase().replace(/[\s-]+/g, '');
    const match = normalized.match(/^(casual|everyday|formal|holiday|party|vacation)dress(?:es)?$/);
    if (!match) return null;
    const url = new URL(action, window.location.origin);
    url.pathname = url.pathname.replace(/\/search\/?$/, '') + '/collections/dresses';
    url.search = '';
    url.searchParams.set('filter.v.t.shopify.dress-occasion', this.occasions[match[1]]);
    url.searchParams.set('filter.v.availability', '1');
    return url;
  }
};

const clotheWeddingSearch = {
  action: document.querySelector('form[role="search"]').action,
  helpURL(term, action) {
    const normalized = term.trim().toLowerCase().replace(/[\s-]+/g, '');
    let page;
    if (/^(?:returns?|returnpolicy|exchanges?|refunds?)$/.test(normalized)) page = 'returnpolicy';
    else if (/^(?:shipping|shippingpolicy|shippinginfo|shippinginformation|delivery|deliveryinfo|deliveryinformation)$/.test(normalized)) page = 'shipping-policy';
    else if (/^(?:contact|contactus|customerservice|customersupport)$/.test(normalized)) page = 'contactus';
    else if (/^(?:locations?|storelocations?|stores?|ourstores|storehours|hours|fayetteville|rogers|springfield)$/.test(normalized)) page = 'contactus';
    else return null;
    const url = new URL(action, window.location.origin);
    url.pathname = url.pathname.replace(/\/search\/?$/, '') + '/pages/' + page;
    url.search = '';
    return url;
  },
  giftMatches(term) {
    return /^gift[ -]?cards?$/.test(clotheSearchPhrase(term));
  },
  giftURL(term, action) {
    if (!this.giftMatches(term)) return null;
    const url = new URL(action, window.location.origin);
    url.pathname = url.pathname.replace(/\/search\/?$/, '') + '/collections/gift-cards';
    url.search = '';
    return url;
  },
  query: "wedding title:dress -title:white -title:ivory -title:cream -title:bridal -title:bride",
  occasion: "gid://shopify/Metaobject/55881138398",
  matches(term) {
    return /^wedding[ -]guests?(?: (?:dress(?:es)?|outfits?))?$/.test(clotheSearchPhrase(term));
  },
  url(term, action) {
    if (!this.matches(term)) return null;
    const url = new URL(action, window.location.origin);
    url.search = '';
    url.searchParams.set('q', this.query);
    url.searchParams.set('type', 'product');
    url.searchParams.set('filter.v.t.shopify.dress-occasion', this.occasion);
    return url;
  }
};

document.addEventListener('submit', (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || form.getAttribute('role') !== 'search') return;
  const input = form.querySelector('input[type="search"][name="q"]');
  if (!input || form.querySelector('[aria-selected="true"] a')) return;
  const url = clotheWeddingSearch.helpURL(input.value, form.action) || clotheWeddingSearch.giftURL(input.value, form.action) || clotheDressSearch.url(input.value, form.action) || clotheTeeSearch.url(input.value, form.action) || clotheSweatshirtSearch.url(input.value, form.action) || clotheHoodieSearch.url(input.value, form.action) || clotheCategorySearch.url(input.value, form.action) || clotheSetSearch.url(input.value, form.action) || clotheProductSearch.url(input.value, form.action) || clotheWeddingSearch.url(input.value, form.action);
  if (!url) return;
  event.preventDefault();
  if (url.hash) form.closest('details-modal')?.close(false);
  window.location.assign(url.href);
}, true);

const clotheSearchLocation = new URL(window.location.href);
if (clotheSearchLocation.searchParams.get('q') === clotheWeddingSearch.query &&
    clotheSearchLocation.searchParams.get('filter.v.t.shopify.dress-occasion') === clotheWeddingSearch.occasion ||
    (clotheSearchLocation.searchParams.get('q') || '').startsWith(clotheProductSearch.query) ||
    clotheTeeSearch.isQuery(clotheSearchLocation.searchParams.get('q') || '') ||
    clotheSweatshirtSearch.isQuery(clotheSearchLocation.searchParams.get('q') || '') ||
    clotheHoodieSearch.isQuery(clotheSearchLocation.searchParams.get('q') || '') || clotheCategorySearch.isQuery(clotheSearchLocation.searchParams.get('q') || '')) {
  const label = document.querySelector('main input[type="search"][name="q"]')?.value;
  if (label) document.querySelectorAll('form[role="search"] input[type="search"][name="q"]').forEach((input) => {
    input.value = label;
    input.defaultValue = label;
  });
}
if (clotheSearchLocation.pathname === new URL(clotheWeddingSearch.action, window.location.origin).pathname) {
  const clotheTeeLocation = clotheTeeSearch.url(clotheSearchLocation.searchParams.get('q') || '', clotheWeddingSearch.action);
  if (clotheTeeLocation) window.location.replace(clotheTeeLocation.href);
  const clotheSweatshirtLocation = clotheSweatshirtSearch.url(clotheSearchLocation.searchParams.get('q') || '', clotheWeddingSearch.action);
  if (clotheSweatshirtLocation) window.location.replace(clotheSweatshirtLocation.href);
  const clotheHoodieLocation = clotheHoodieSearch.url(clotheSearchLocation.searchParams.get('q') || '', clotheWeddingSearch.action);
  if (clotheHoodieLocation) window.location.replace(clotheHoodieLocation.href);
  const clotheCategoryLocation = clotheCategorySearch.url(clotheSearchLocation.searchParams.get('q') || '', clotheWeddingSearch.action);
  if (clotheCategoryLocation) window.location.replace(clotheCategoryLocation.href);
  const clotheSetLocation = clotheSetSearch.url(clotheSearchLocation.searchParams.get('q') || '', clotheWeddingSearch.action);
  if (clotheSetLocation) window.location.replace(clotheSetLocation.href);
  const clotheProductLocation = clotheProductSearch.url(clotheSearchLocation.searchParams.get('q') || '', clotheWeddingSearch.action);
  if (clotheProductLocation) window.location.replace(clotheProductLocation.href);
  const clotheDressLocation = clotheDressSearch.url(clotheSearchLocation.searchParams.get('q') || '', clotheWeddingSearch.action);
  if (clotheDressLocation) window.location.replace(clotheDressLocation.href);
  const clotheWeddingLocation = clotheWeddingSearch.url(clotheSearchLocation.searchParams.get('q') || '', clotheWeddingSearch.action);
  if (clotheWeddingLocation) window.location.replace(clotheWeddingLocation.href);
}



 document.addEventListener('click', (event) => {
  const link = event.target.closest?.('a[href]');
  if (!link || !link.closest('details-modal')) return;
  const url = new URL(link.href, window.location.origin);
  if (url.origin === window.location.origin && url.pathname === window.location.pathname && /^#(?:our-stores|fayetteville|rogers|springfield)$/.test(url.hash)) link.closest('details-modal').close(false);
 });


